import React, { Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import * as Sentry from "@sentry/react";
import { AppProps } from 'web/AppProps'
import { Layout } from 'web/Layout'
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { useCurrentUser } from './hooks/useCurrentUser'
import Header, { BackTo } from './components/Header';
import { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { DeviceMonitoringProvider } from './contexts/DeviceMonitoringContext';

// Lazy load all page components with proper export handling
const DeviceDataPage = React.lazy(() => import('web/pages/device-data/DeviceDataPage').then(m => ({ default: m.DeviceDataPage })));
const DevicesPage = React.lazy(() => import('web/pages/devices/DevicesPage').then(m => ({ default: m.DevicesPage })));
const UsersPage = React.lazy(() => import('web/pages/admin/users/UsersPage').then(m => ({ default: m.UsersPage })));
const OrganizationsPageForAdmin = React.lazy(() => import('web/pages/admin/organizations/OrganizationsPage').then(m => ({ default: m.OrganizationsPageForAdmin })));
const PatientsPage = React.lazy(() => import('web/pages/patients/PatientsPage').then(m => ({ default: m.PatientsPage })));
const PatientDetailPage = React.lazy(() => import('web/pages/patients/PatientDetailPage').then(m => ({ default: m.PatientDetailPage })));
const BillingPage = React.lazy(() => import('web/pages/billing/BillingPage').then(m => ({ default: m.BillingPage })));
const DeviceLambdaSettingPage = React.lazy(() => import('./pages/device-lambda-setting/DeviceLambdaSettingPage').then(m => ({ default: m.DeviceLambdaSettingPage })));
const LabelDetailPage = React.lazy(() => import('./pages/device-data/LabelDetailPage').then(m => ({ default: m.LabelDetailPage })));
const DeviceDetailPage = React.lazy(() => import('./pages/devices/DeviceDetailPage'));
const LabelDataPage = React.lazy(() => import('./pages/device-data/LabelDataPage').then(m => ({ default: m.LabelDataPage })));
const MyProfilePage = React.lazy(() => import('./pages/my-profile/MyProfilePage').then(m => ({ default: m.MyProfilePage })));
const LabelsPage = React.lazy(() => import('./pages/labels/LabelsPage').then(m => ({ default: m.LabelsPage })));
const DeviceTemplateDetailPage = React.lazy(() => import('./pages/device-templates/DeviceTemplateDetailPage'));
const DeviceTemplatePage = React.lazy(() => import('./pages/device-templates/DeviceTemplatesPage').then(m => ({ default: m.DeviceTemplatePage })));
const OrganizationsPage = React.lazy(() => import('./pages/organizations/OrganizationsPage').then(m => ({ default: m.OrganizationsPage })));
const MedicalRecordTemplatePage = React.lazy(() => import('web/pages/medical-record-template/MedicalRecordTemplatePage'));
const MedicalRecordTemplateWizard = React.lazy(() => import('web/pages/medical-record-template/MedicalRecordTemplateWizard'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const StartSubscriptionPage = React.lazy(() => import('./pages/StartSubscriptionPage'));
const NoOrganizationPage = React.lazy(() => import('./pages/NoOrganizationPage'));
const MonitoringPanelPage = React.lazy(() => import('web/pages/MonitoringPanelPage'));
const ModifiedPatientNotesPage = React.lazy(() => import('./pages/admin/modified-patient-notes/ModifiedPatientNotesPage'));
const OrganizationStatisticsPage = React.lazy(() => import('./pages/OrganizationStatisticsPage'));
const OrganizationDetailPageForAdmin = React.lazy(() => import('./pages/admin/organizations/OrganizationDetailPage').then(m => ({ default: m.OrganizationDetailPageForAdmin })));
const NXVETDevicesPage = React.lazy(() => import('./pages/nxvet-devices/NXVETDevicesPage').then(m => ({ default: m.NXVETDevicesPage })));
const OrganizationPreferencesPage = React.lazy(() => import('./pages/organizations/OrganizationPreferencesPage'));
const OrganizationAdminStatisticsPage = React.lazy(() => import('./pages/admin/organizations/OrganizationAdminStatisticsPage'));
const ThankYouPage = React.lazy(() => import('./pages/ThankYouPage').then(m => ({ default: m.ThankYouPage })));
const UserDetailPage = React.lazy(() => import('./pages/admin/users/UserDetailPage'));
const ReferralPage = React.lazy(() => import('./pages/ReferralPage'));
const ReferralListPage = React.lazy(() => import('./pages/admin/ReferralListPage'));
const DashboardPage = React.lazy(() => import('./pages/dashboard/DashboardPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const AccountSettingPage = React.lazy(() => import('./pages/AccountSettingPage'));
const ActivityManagerPage = React.lazy(() => import('./pages/activity-manager/ActivityManagerPage').then(m => ({ default: m.ActivityManagerPage })));
const NXVETCallsPage = React.lazy(() => import('./pages/nxvet-calls/NXVETCallsPage').then(m => ({ default: m.NXVETCallsPage })));
const AudioRecordingsPage = React.lazy(() => import('./pages/audio-recordings/AudioRecordingsPage').then(m => ({ default: m.AudioRecordingsPage })));
const DocumentsPage = React.lazy(() => import('./pages/documents/DocumentsPage').then(m => ({ default: m.DocumentsPage })));
const NxscopeFirmwarePage = React.lazy(() => import('./pages/admin/NxscopeFirmwarePage'));
const NxhubFirmwarePage = React.lazy(() => import('./pages/admin/NxhubFirmwarePage'));
const GrowthStatisticsPage = React.lazy(() => import('./pages/admin/GrowthStatisticsPage'));
const NxScopeLogsPage = React.lazy(() => import('./pages/admin/nxscope-logs/NxScopeLogsPage').then(m => ({ default: m.NxScopeLogsPage })));
const LabelCalibrationList = React.lazy(() => import('./pages/labels/LabelCalibrationList').then(m => ({ default: m.LabelCalibrationList })));
const SpeakerIdentificationPage = React.lazy(() => import('./pages/speaker-identification/SpeakerIdentificationPage').then(m => ({ default: m.SpeakerIdentificationPage })));
const IntegrationsPage = React.lazy(() => import('./pages/integrations/IntegrationsPage'));

const createPageViewTrackingRouter = <P extends Record<string, any>> (Routes: React.FC<P>) => {
    return (props: P) => {
        const location = useLocation();

        useEffect(() => {
            TagManager.dataLayer({
                dataLayer: {
                    event: "page_view",
                    page_title: document.title,
                    page_location: location.pathname,
                },
            });
        }, [location]);

        return <Routes {...props} />;
    };
}

export const App = (props: AppProps) => {
    const context = props.context
    const user = useCurrentUser();
    const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
    const WrappedRouter = createPageViewTrackingRouter(SentryRoutes);

    return (
        <DeviceMonitoringProvider>
            <BrowserRouter>
                <QueryParamProvider adapter={ReactRouter6Adapter}>
                    <Suspense fallback={
                        <div className="flex items-center justify-center min-h-screen">
                            <div className="loading loading-spinner loading-lg"></div>
                        </div>
                    }>
                        <WrappedRouter>
                            <Route path="/" element={<Layout />}>
                            <Route path="" element={<Navigate to={user.isAdmin ? "device-data" : "patients"} replace />} />
                            <Route path="patients/*">
                                <Route path="new" element={<RouteWithHeader backTo={{url: "/patients", pageName: "Patients"}}><PatientDetailPage /></RouteWithHeader>} />
                                <Route path=":id" element={<RouteWithHeader backTo={{url: "/patients", pageName: "Patients"}}><PatientDetailPage /></RouteWithHeader>} />
                                <Route index element={<RouteWithHeader><PatientsPage /></RouteWithHeader>} />
                            </Route>
                            <Route path="labels/*">
                                <Route index element={<RouteWithHeader><LabelsPage /></RouteWithHeader>} />
                            </Route>
                            <Route path="lambda-setting">
                                <Route index element={<RouteWithHeader><DeviceLambdaSettingPage context={context} /></RouteWithHeader>} />
                            </Route>
                            <Route path="devices/*">
                                <Route path=":id" element={<RouteWithHeader backTo={{url: "/devices", pageName: "Devices"}}><DeviceDetailPage /></RouteWithHeader>} />
                                <Route path=":deviceSerial/labels/:labelId/data" element={<RouteWithHeader><LabelDataPage context={context} /></RouteWithHeader>} />
                                <Route index element={<RouteWithHeader><DevicesPage context={context} /></RouteWithHeader>} />
                            </Route>
                            <Route path="device-templates/*">
                                <Route path=":id" element={<RouteWithHeader backTo={{url: "/device-templates", pageName: "Device Templates"}}><DeviceTemplateDetailPage /></RouteWithHeader>} />
                                <Route index element={<RouteWithHeader><DeviceTemplatePage context={context} /></RouteWithHeader>} />
                            </Route>
                            <Route path="billing/*">
                                <Route index element={<RouteWithHeader><BillingPage /></RouteWithHeader>} />
                            </Route>
                            <Route path="dashboard/*">
                                <Route index element={<RouteWithHeader><DashboardPage /></RouteWithHeader>} />
                            </Route>
                            <Route path="statistics/*">
                                <Route index element={<RouteWithHeader><OrganizationStatisticsPage /></RouteWithHeader>} />
                            </Route>
                            <Route path="monitoring-panel/*">
                                <Route index element={<RouteWithHeader><MonitoringPanelPage context={context} /></RouteWithHeader>} />
                            </Route>

                            <Route path="device-data/*">
                                <Route path=":id/data" element={<RouteWithHeader><DeviceDataPage context={context} /></RouteWithHeader>} />
                                <Route index element={<RouteWithHeader><DeviceDataPage context={context} /></RouteWithHeader>} />
                            </Route>
                            <Route path="labels/*">
                                <Route path=":id/detail" element={<LabelDetailPage context={context} />} />
                            </Route>
                            <Route path="admin/*">
                                <Route path="organizations/*">
                                    <Route path=":id" element={<RouteWithHeader><OrganizationDetailPageForAdmin /></RouteWithHeader>} />
                                    <Route index element={<RouteWithHeader><OrganizationsPageForAdmin /></RouteWithHeader>} />
                                </Route>
                                <Route path="modified-patient-notes/*">
                                    <Route index element={<RouteWithHeader><ModifiedPatientNotesPage /></RouteWithHeader>} />
                                </Route>
                                <Route path="statistics" element={<RouteWithHeader><OrganizationAdminStatisticsPage /></RouteWithHeader>} />
                                <Route path="users/*">
                                    <Route path=":id" element={<RouteWithHeader><UserDetailPage /></RouteWithHeader>} />
                                    <Route index element={<RouteWithHeader><UsersPage /></RouteWithHeader>} />
                                </Route>
                                <Route path="referrals" element={<RouteWithHeader><ReferralListPage /></RouteWithHeader>} />
                                <Route path="nxscope-firmware" element={<RouteWithHeader><NxscopeFirmwarePage /></RouteWithHeader>} />
                                <Route path="nxhub-firmware" element={<RouteWithHeader><NxhubFirmwarePage /></RouteWithHeader>} />
                                <Route path="growth-statistics" element={<RouteWithHeader><GrowthStatisticsPage /></RouteWithHeader>} />
                                <Route path="nxscope-logs" element={<RouteWithHeader><NxScopeLogsPage /></RouteWithHeader>} />
                            </Route>
                            <Route path="preferences">
                                <Route index element={<RouteWithHeader><OrganizationPreferencesPage /></RouteWithHeader>} />
                            </Route>
                            <Route path="organization/*">
                                <Route index element={<RouteWithHeader><OrganizationsPage /></RouteWithHeader>} />
                            </Route>
                            <Route path="my-profile/*">
                                <Route index element={<RouteWithHeader><MyProfilePage /></RouteWithHeader>} />
                            </Route>
                            <Route path="labels-calibration" element={<RouteWithHeader><LabelCalibrationList /></RouteWithHeader>} />
                            <Route path="thank-you" element={<RouteWithHeader><ThankYouPage /></RouteWithHeader>} />
                            <Route path="medical-record-template" element={<RouteWithHeader><MedicalRecordTemplatePage context={context} /></RouteWithHeader>} />
                            <Route path="medical-record-template/wizard" element={<RouteWithHeader><MedicalRecordTemplateWizard /></RouteWithHeader>} />
                            <Route path="start-subscription" element={<RouteWithHeader><StartSubscriptionPage /></RouteWithHeader>} />
                            <Route path="no-organization" element={<RouteWithHeader><NoOrganizationPage /></RouteWithHeader>} />
                            <Route path="review-center" element={<RouteWithHeader><ActivityManagerPage /></RouteWithHeader>} />
                            <Route path="nxvet-devices" element={<RouteWithHeader><NXVETDevicesPage /></RouteWithHeader>} />
                            <Route path="nxvet-calls" element={<RouteWithHeader><NXVETCallsPage /></RouteWithHeader>} />
                            <Route path="audio-recordings" element={<RouteWithHeader><AudioRecordingsPage /></RouteWithHeader>} />
                            <Route path="speaker-identification" element={<RouteWithHeader><SpeakerIdentificationPage /></RouteWithHeader>} />
                            <Route path="documents" element={<RouteWithHeader><DocumentsPage /></RouteWithHeader>} />
                            <Route path="integrations" element={<RouteWithHeader><IntegrationsPage /></RouteWithHeader>} />
                            <Route path="referral" element={<RouteWithHeader><ReferralPage /></RouteWithHeader>} />
                            <Route path="profile" element={<RouteWithHeader><ProfilePage /></RouteWithHeader>} />
                            <Route path="account-setting" element={<RouteWithHeader><AccountSettingPage /></RouteWithHeader>} />
                            <Route path='*' element={<NotFoundPage />} />
                        </Route>
                        </WrappedRouter>
                    </Suspense>
                </QueryParamProvider>
            </BrowserRouter>
        </DeviceMonitoringProvider>
    )
}

const RouteWithHeader = ({backTo, children}: {backTo?: BackTo, children: React.ReactNode}) => {
    return (
        <div>
            <Header backTo={backTo} />
            {children}
        </div>
    )
}
